import React from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import {
  DatePicker,
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { postUnpauseActivity } from 'services/activity';
import * as yup from 'yup';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import useStyles from './style';
import BaseModal from '../BaseModal';
import { IModalUnpauseProps } from './types';

const FORM_ID = 'RETOMAR_FORM_MODAL';

const MAX_DATE = dayjs('2100-01-01');
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido';

const ModalUnpause = ({
  children,
  open,
  onClose,
  gridRow,
  reloadData,
}: IModalUnpauseProps) => {
  const classes = useStyles();
  const MIN_DATE = dayjs(gridRow?.pauseStart).add(3, 'hour');
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const validationSchema = yup.object({
    pauseEnd: yup
      .mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        // const minDate = dayjs(gridRow?.pauseStart).add(3, 'hour');
        const test = dayjs(value);

        const validation =
          // testa a data e verifica a validação de data mínima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          test.isValid() &&
          (test.isSame(MIN_DATE, 'day') || test.isAfter(MIN_DATE, 'day')) &&
          // testa a data e verifica a validação de data máxima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          (test.isSame(MAX_DATE, 'day') || test.isBefore(MAX_DATE, 'day'));

        return validation;
      }),
  });

  const formik = useFormik({
    initialValues: {
      activity: '',
      equipment: '',
      pauseStart: '',
      pauseEnd: null,
    },
    validationSchema,
    onSubmit: formValues => {
      const { pauseEnd } = formValues;

      const params = {
        id: gridRow?.activityRealId,
        completionOfPause: dayjs(pauseEnd).utc().format('YYYY-MM-DDTHH:mmZ'),
      };

      // se for enviado com sucesso
      setLoadingSaveForm(true);
      postUnpauseActivity(params)
        .then(() => {
          // solicita uma recarga de dados
          if (reloadData) reloadData();
          // fecha o formulário
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  React.useEffect(() => {
    if (!open) {
      if (formik.dirty || !formik.isValid) {
        formik.resetForm();
      }
    }
  }, [formik, open]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para retomar a atividade:
            </Typography>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Atividate"
                variant="outlined"
                value={gridRow?.activity}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Equipamento"
                variant="outlined"
                value={gridRow?.equipment}
                disabled
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePicker
                id="pauseStart"
                name="pauseStart"
                label="Início da pausa"
                inputVariant="outlined"
                disabled
                format="DD/MM"
                value={dayjs(gridRow?.pauseStart)}
                onChange={date => handleDateChange('pauseStart', date)}
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                name="pauseEnd"
                value={formik.values.pauseEnd}
                onChange={date => handleDateChange('pauseEnd', date)}
                inputVariant="outlined"
                label="Término da pausa"
                minDate={MIN_DATE}
                minDateMessage="Preenchimento inválido."
                invalidDateMessage="Preenchimento inválido."
                maxDateMessage="A data não deve ser posterior à data máxima"
                format="DD/MM/YYYY"
                clearable
                clearLabel="Limpar"
                cancelLabel="Cancelar"
                className={classes.root}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.pauseEnd && Boolean(formik.errors.pauseEnd)
                }
                helperText={
                  (formik.touched.pauseEnd && formik.errors.pauseEnd) ||
                  DEFAULT_OK_TEXT
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }, [
    MIN_DATE,
    classes.bodyContent,
    classes.formMessage,
    classes.root,
    formik.errors.pauseEnd,
    formik.handleBlur,
    formik.handleSubmit,
    formik.touched.pauseEnd,
    formik.values.pauseEnd,
    gridRow?.activity,
    gridRow?.equipment,
    gridRow?.pauseStart,
    handleDateChange,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          form={FORM_ID}
          disabled={!formik.dirty || !formik.isValid}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <BaseModal
          title="Retomar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="629px"
        >
          {children}
        </BaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};

export default ModalUnpause;
